import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputLabel,
  FormControl,
} from "@mui/material";
import TextInput, { BootstrapInput } from "../../../Component/UI/TextInput/TextInput";
import InputSelectionComponent from "../../Dashboard/Component/inputSelection";
import api from "../../../BrandOnBoarding/api";
import classes from "../settings.module.css";
import styles from "./ConversationMonitoringForm.module.css";

const TYPE_OPTIONS = ["brand", "product"];
const QUERY_TYPE_OPTIONS = ["brand", "category"];

// Mirrors BootstrapInput's box (border/radius/font-size/padding) so the site-suggestions
// picker sits at the same height/width as every other field in this form.
const siteSuggestionsBoxStyle = {
  marginTop: 20,
  border: "1px solid #E0E3E7",
  borderRadius: 4,
  padding: "4px 10px",
  minHeight: 34,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
};

const ConversationMonitoringForm = ({
  customers,
  formValues,
  setFormValues,
  isUpdateMode,
  allowCustomerSelection = true,
  handleCloseModal,
  onSubmit,
}) => {
  const [siteSuggestionOptions, setSiteSuggestionOptions] = useState([]);
  // Tracks the customer_id the form last fetched options for, so we can tell a real
  // switch (clear stale selections) apart from the initial mount (keep prefilled ones).
  const previousCustomerIdRef = useRef(formValues.customer_id);

  // Same source as the Conversations nav bar: GET /customers/:id/site-suggestions,
  // backed by the customer table's customer_ui_info.site_suggestions.
  useEffect(() => {
    const customerChanged =
      previousCustomerIdRef.current && previousCustomerIdRef.current !== formValues.customer_id;
    previousCustomerIdRef.current = formValues.customer_id;

    if (customerChanged) {
      setFormValues((prev) => ({ ...prev, site_suggestions: [] }));
    }

    if (!formValues.customer_id) {
      setSiteSuggestionOptions([]);
      return;
    }
    let cancelled = false;
    api
      .get(`/customers/${formValues.customer_id}/site-suggestions`)
      .then((response) => {
        if (!cancelled) setSiteSuggestionOptions(response?.data?.siteSuggestions || []);
      })
      .catch(() => {
        if (!cancelled) setSiteSuggestionOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formValues.customer_id]);

  const handleChange = (field) => (e) => {
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckboxChange = (field) => (e) => {
    setFormValues((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSiteSuggestionsChange = (selected) => {
    const selectedValues = selected?.map((option) => option.value) || [];
    setFormValues((prev) => ({ ...prev, site_suggestions: selectedValues }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  const selectedCustomerName = customers?.find(
    (c) => c.id === formValues.customer_id
  )?.name;

  return (
    <form onSubmit={handleFormSubmit}>
      <div className={classes.divContainer}>
        {isUpdateMode ? (
          // Editing an existing config: show which customer it belongs to, read-only.
          <TextInput label="Customer" value={selectedCustomerName || ""} disabled fullWidth />
        ) : allowCustomerSelection ? (
          <FormControl variant="standard" size="small" fullWidth>
            <InputLabel shrink>Customer</InputLabel>
            <Select
              value={formValues.customer_id || ""}
              onChange={handleChange("customer_id")}
              input={<BootstrapInput />}
              displayEmpty
              required
            >
              <MenuItem value="" disabled>
                Select a customer
              </MenuItem>
              {customers?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null /* Creating as a Company admin: customer is implicit, no field needed */}

        <TextInput
          label="Category"
          fullWidth
          required
          value={formValues.category || ""}
          onChange={handleChange("category")}
        />
        <TextInput
          label="Brand / Entity"
          fullWidth
          required
          value={formValues.entity || ""}
          onChange={handleChange("entity")}
        />
        <TextInput
          label="Persona"
          fullWidth
          value={formValues.persona || ""}
          onChange={handleChange("persona")}
        />

        <FormControl variant="standard" size="small" fullWidth>
          <InputLabel shrink>Type</InputLabel>
          <Select
            value={formValues.type || "brand"}
            onChange={handleChange("type")}
            input={<BootstrapInput />}
            required
          >
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" size="small" fullWidth>
          <InputLabel shrink>Query Type</InputLabel>
          <Select
            value={formValues.query_type || "brand"}
            onChange={handleChange("query_type")}
            input={<BootstrapInput />}
            required
          >
            {QUERY_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="standard" size="small" fullWidth>
          <InputLabel shrink>Site Suggestions</InputLabel>
          <div style={siteSuggestionsBoxStyle} className={styles.siteSuggestionsBox}>
            <InputSelectionComponent
              type="site"
              options={siteSuggestionOptions}
              selectedOptions={formValues.site_suggestions || []}
              handleChange={handleSiteSuggestionsChange}
              disabled={!formValues.customer_id}
            />
          </div>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={!!formValues.exclude_brand_owned_sources}
              onChange={handleCheckboxChange("exclude_brand_owned_sources")}
            />
          }
          label="Exclude brand-owned sources"
        />

        <div className={classes.buttonDiv}>
          <Button type="submit" variant="contained">
            {isUpdateMode ? "Update" : "Add"}
          </Button>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ConversationMonitoringForm;
